import { NotificationDay, NotificationFrequency, NotificationMethod } from "@/contexts/auth/enums";
import { User, NotificationPreferences } from "../contexts/auth/types";
import { Event } from "../lib/types";
import { showBrowserNotification } from './browserNotificationService';
import {
  replaceTemplateVariables,
  formatEmailDate,
  generateTrackingPixel,
  generateUnsubscribeUrl,
  getNotificationEmailTemplate
} from '@/emails/utils/emailTemplateHelpers';
import { formatEventDate, formatEventTime } from '@/lib/eventHelpers';

// This would be replaced with actual email sending logic in production
export const sendEmailNotification = async (
  user: User,
  events: Event[],
  recommendedEvents: Event[] = []
): Promise<boolean> => {
  console.log(`Sending email notification to ${user.email}`);
  console.log('Events:', events);
  console.log('Recommended events:', recommendedEvents);

  // In a real implementation, this would connect to an email service like SendGrid
  // Format events for email
  const formattedEvents = events.map(event => ({
    name: event.name,
    type: event.type,
    date: `${formatEventDate(event.dateStart)} ${formatEventTime(event.dateStart)}`,
    location: event.venue.online ? 'Online' : `${event.venue.name}, ${event.venue.city}`,
    link: `https://medihub.hu/events/${event.id}`
  }));

  const formattedRecommendedEvents = recommendedEvents.map(event => ({
    name: event.name,
    type: event.type,
    date: `${formatEventDate(event.dateStart)} ${formatEventTime(event.dateStart)}`,
    location: event.venue.online ? 'Online' : `${event.venue.name}, ${event.venue.city}`,
    link: `https://medihub.hu/events/${event.id}`
  }));

  // Common email template variables
  const emailVariables = {
    name: user.userProfile?.name?.firstName || 'Felhasználó',
    emailDate: formatEmailDate(new Date(), true),
    eventCount: events.length,
    recommendedCount: recommendedEvents.length,
    unsubscribeUrl: generateUnsubscribeUrl(user['@id'] || '', 'notification'),
    trackingPixel: generateTrackingPixel('notification-' + Date.now(), user['@id'] || ''),
    privacyPolicyUrl: 'https://medihub.hu/privacy-policy',
    termsUrl: 'https://medihub.hu/terms'
  };

  // Generate the complete HTML email using our bulletproof template
  const emailContent = getNotificationEmailTemplate(
    emailVariables,
    formattedEvents,
    formattedRecommendedEvents
  );

  // In production, we would send via API:
  // return sendGridService.send(user.email, 'Új események a MediHub-on', emailContent);

  return true;
};

export const sendBrowserNotification = async (
  user: User,
  events: Event[],
  recommendedEvents: Event[] = []
): Promise<boolean> => {
  if (!events.length && !recommendedEvents.length) {
    return false;
  }

  // Create a summary for the notification
  const totalEvents = events.length + recommendedEvents.length;
  const title = `Medihub - ${totalEvents} új esemény`;
  const body = events.length
    ? `${events.length} új esemény az általad kedvelt kategóriákban`
    : '';
  const bodyRecommended = recommendedEvents.length
    ? `${recommendedEvents.length} ajánlott esemény számodra`
    : '';

  const options = {
    body: [body, bodyRecommended].filter(Boolean).join('\n'),
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: 'medihub-events',
    renotify: true
  };

  const notification = showBrowserNotification(title, options);
  return !!notification;
};

export const sendNotification = async (
  user: User,
  events: Event[],
  recommendedEvents: Event[] = []
): Promise<boolean> => {
  if (!user.userProfile.notificationPreferences?.enabled) {
    return false;
  }

  const method = user.userProfile.notificationPreferences.notificationMethod;
  let success = false;

  if (method === 'email' || method === 'both') {
    success = await sendEmailNotification(user, events, recommendedEvents);
  }

  if (method === 'browser' || method === 'both') {
    success = await sendBrowserNotification(user, events, recommendedEvents) || success;
  }

  return success;
};

export const getDefaultNotificationPreferences = (): NotificationPreferences => {
  return {
    frequency: NotificationFrequency.MONTHLY,
    preferredTopics: [],
    notificationMethod: NotificationMethod.EMAIL,
    enabled: true,
    notificationDay: NotificationDay.MONDAY,
    notificationTime: '10:00'
  };
};

export const getRecommendedEvents = (
  allEvents: Event[],
  userPreferences: NotificationPreferences
): Event[] => {
  if (!userPreferences.preferredTopics.length) {
    return [];
  }

  // Simple recommendation logic based on preferred topics and event tags
  return allEvents.filter(event => {
    // Check if any of the user's preferred topics match the event tags or type
    return userPreferences.preferredTopics.some((topic: string) =>
      (event.professions && event.professions.includes(topic))
    );
  });
};

export const generateNotificationContent = (
  event: Event,
  isRecommended: boolean = false
): string => {
  const venueInfo = event.venue.online
    ? 'Online esemény'
    : `${event.venue.name}, ${event.venue.city}`;

  const startDate = new Date(event.dateStart);
  const formattedDate = startDate.toLocaleDateString('hu-HU', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });

  // Limit description to 2-3 sentences
  let shortDescription = event.description.split(/[.!?]/).slice(0, 2).join('. ') + '.';
  if (shortDescription.length > 150) {
    shortDescription = shortDescription.substring(0, 147) + '...';
  }

  const recommendedBadge = isRecommended ? '⭐ AJÁNLOTT ESEMÉNY ⭐\n' : '';

  return `${recommendedBadge}Esemény: ${event.name}
Típus: ${event.type}
Időpont: ${formattedDate}
Helyszín: ${venueInfo}

${shortDescription}

További részletek: [link az eseményhez]`;
};
